import { useNavigate } from "react-router-dom";

const AboutUs = () => {
    const navigate = useNavigate();

  return (
    <div className="container">
      <div className="bg-white rounded-4 shadow-lg p-5 border-0 mt-5 mb-5 overflow-hidden">
        {/* Hero Section */}
        <section className="text-center mb-5">
          <h1 className="fw-bold mb-3">About SampleFiles.dev</h1>
          <p className="text-body-secondary fw-medium fs-5">
            Ever spent hours creating dummy files just to test a simple upload feature? So did we.
          </p>
          <p className="text-body-secondary fw-medium">
            That's why we built <strong>SampleFiles.dev</strong> — to save developers from the tedious work of generating test files, so you can focus on what matters: building great software.
          </p>
        </section>

        {/* Our Story */}
        <section className="mb-5">
          <h2 className="fw-semibold mb-3">Our Story</h2>
          <p>
            SampleFiles.dev started when we were building file upload features for client projects. 
            We kept wasting precious time creating sample PDFs, CSVs, and images instead of writing 
            actual code. After searching for a reliable source of test files and coming up short, 
            we decided to build the solution we needed.
          </p>
          <p>
            What began as a personal toolkit has grown into a community resource used by thousands of 
            developers worldwide. We're still an independent project run by developers who understand 
            your pain points because we face them every day.
          </p>
        </section>

        {/* What We Offer */}
        <section className="mb-5">
          <h2 className="fw-semibold mb-3">What We Offer</h2>
          <div className="row g-3">
            <div className="col-md-6">
              <div className="d-flex align-items-start">
                <span className="me-3">🖼️</span>
                <div>
                  <strong>Sample Images</strong>
                  <p className="mb-0 text-body-secondary">JPG, PNG, SVG, GIF, WEBP, TIFF, and more</p>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="d-flex align-items-start">
                <span className="me-3">🎵</span>
                <div>
                  <strong>Sample Audio Files</strong>
                  <p className="mb-0 text-body-secondary">MP3, WAV, OGG, M4A for media player testing</p>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="d-flex align-items-start">
                <span className="me-3">🎬</span>
                <div>
                  <strong>Sample Video Files</strong>
                  <p className="mb-0 text-body-secondary">MP4, MOV, WEBM, AVI in different resolutions</p>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="d-flex align-items-start">
                <span className="me-3">📄</span>
                <div>
                  <strong>Sample Documents</strong>
                  <p className="mb-0 text-body-secondary">PDF, DOCX, XLSX, CSV, TXT for upload & export testing</p>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="d-flex align-items-start">
                <span className="me-3">🗂️</span>
                <div>
                  <strong>Sample Archives</strong>
                  <p className="mb-0 text-body-secondary">ZIP, RAR, TAR, and other compressed formats</p>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="d-flex align-items-start">
                <span className="me-3">🔧</span>
                <div>
                  <strong>Developer Tools</strong>
                  <p className="mb-0 text-body-secondary">Online tools to generate and convert test files</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Developers Love Us */}
        <section className="mb-5">
          <h2 className="fw-semibold mb-3">Why Developers Love Us</h2>
          <div className="row g-4">
            <div className="col-md-6">
              <div className="card border-0 bg-light h-100">
                <div className="card-body">
                  <h5 className="card-title">🚀 Save Development Time</h5>
                  <p className="card-text mb-0">No more manually creating test files. Get back to coding in seconds with our ready-to-use samples.</p>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-0 bg-light h-100">
                <div className="card-body">
                  <h5 className="card-title">🐛 Test Edge Cases</h5>
                  <p className="card-text mb-0">Our files include the tricky cases that break production code - large files, weird encodings, and malformed data.</p>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-0 bg-light h-100">
                <div className="card-body">
                  <h5 className="card-title">🛡️ Safe & Reliable</h5>
                  <p className="card-text mb-0">Every file is virus-scanned and free from sensitive or copyrighted content. Safe for any environment.</p>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-0 bg-light h-100">
                <div className="card-body">
                  <h5 className="card-title">⚡ No Friction</h5>
                  <p className="card-text mb-0">No signups, no subscriptions, no limits. Just instant access to the files you need.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="mb-5">
          <div className="row">
            <div className="col-md-6">
              <h2 className="fw-semibold mb-3">Our Mission</h2>
              <div className="bg-primary text-white rounded p-4">
                <p className="mb-0 fw-medium">
                  To provide high-quality, virus-free sample files that help developers build better products faster.
                </p>
              </div>
            </div>
            <div className="col-md-6">
              <h2 className="fw-semibold mb-3">Our Vision</h2>
              <p>
                We aim to become the <strong>most trusted resource for sample files worldwide</strong>, 
                supporting developers across web, mobile, and data-driven platforms.
              </p>
              <p className="mb-0">
                Our roadmap includes <strong>API-based file generation</strong> and expanded format support 
                to serve even more development needs.
              </p>
            </div>
          </div>
        </section>

        {/* Social Proof */}
        <section className="mb-5">
          <h2 className="fw-semibold mb-3">Trusted By Developers Worldwide</h2>
          <div className="bg-light rounded p-4">
            <p className="fst-italic mb-3">
              "SampleFiles.dev saved our team hours of work testing our file processing pipeline. 
              The variety of file types and edge cases helped us catch bugs before they reached production."
            </p>
            <p className="fw-bold mb-0">- Jack, Full Stack Developer</p>
          </div>
        </section>

        {/* Call to Action */}
        <section className="mb-5 text-center bg-primary text-white rounded p-5">
          <h2 className="fw-semibold mb-3">Ready to Speed Up Your Development?</h2>
          <p className="mb-4">Stop wasting time on test data and start building features that matter.</p>
          <a href="/" className="btn btn-light btn-lg fw-bold px-4">
            Explore Sample Files
          </a>
        </section>

        {/* Safety & Transparency */}
        <section className="mb-5">
          <h2 className="fw-semibold mb-3">File Safety & Transparency</h2>
          <div className="row">
            <div className="col-md-6">
              <ul className="list-unstyled">
                <li className="mb-2">✅ Free from personal or copyrighted content</li>
                <li className="mb-2">✅ Virus-free and safe for any environment</li>
                <li className="mb-2">✅ Compliant with fair-use guidelines</li>
              </ul>
            </div>
            {/* <div className="col-md-6">
              <div className="bg-warning bg-opacity-10 rounded p-3">
                <p className="mb-0 small">
                  <strong>Found an issue?</strong> Report broken links or unsafe files and we'll fix them immediately.
                </p>
              </div>
            </div> */}
          </div>
        </section>

        {/* Contact */}
        {/* <section className="mb-5">
          <h2 className="fw-semibold mb-3">Get In Touch</h2>
          <p>
            We're always looking to improve and add new file types. Have a suggestion, found a bug, 
            or need a specific file format we don't have yet?
          </p>
          <div className="d-flex gap-3 flex-wrap">
            <a href="mailto:samplefiles.dev@gmail.com" className="btn btn-outline-primary">
              📧 Email Us
            </a>
            <a href="/contact" onClick={()=>{
                     navigate("contact-us");
                  setTimeout(() => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }, 100);
            }} className="btn btn-outline-secondary">
              📋 Contact Form
            </a>
            <a href="/suggest-file" className="btn btn-outline-info">
              💡 Suggest a File Type
            </a>
          </div>
        </section> */}

        {/* Footer */}
        <section className="text-center border-top pt-4">
          <p className="text-body-secondary fw-medium">
            SampleFiles.dev is built for the community — by developers, for developers. 
          </p>
          <p className="mb-0">
            If you find our site helpful, consider sharing it with your peers or linking to it in your tutorials and documentation.
          </p>
        </section>
      </div>
    </div>
  );
};

export default AboutUs;